import { createDraftSafeSelector, createEntityAdapter, createSlice, Draft, EntityState, PayloadAction, Reducer } from "@reduxjs/toolkit";
import { AsyncEntityState, createAsyncEntityInitialState } from "../../../Store/Thunks/asyncEntityStateUtil";
import { ConsumptionsWithIdentifier, fetchConsumptionByIdsThunk } from "../../../Store/Thunks/fetchConsumptionByIds";
import { fetchConsumptionByFilter } from "../../../Store/Thunks/fetchConsumptionByFilters";
import { RootState } from "../../../Store";
import { EnqueueFetcherWithFilterFetch } from "../../../Store/Thunks/enqueueFetcher";
import { entityByIdWithStatusHookFactory } from "../../../Store/Entities/entityByIdWithStatusHookFactory";
import { fetchAnimalsByIdsThunk } from "../../../Store/Thunks/animalsByIds";
import { entitiesByMultipleIdsWithStatusHookFactory } from "../../../Store/Entities/entitiesByMultipleIdsWithStatusHookFactory";

const selectId = (consumption: ConsumptionsWithIdentifier) => consumption.target_id;
const fetchByIdsThunk = fetchConsumptionByIdsThunk;
const fetchByFilterThunk = fetchConsumptionByFilter;
const entityAdapter = createEntityAdapter({selectId});

const {updateMany, updateOne, upsertOne, upsertMany, addOne, addMany, removeAll, removeOne} = entityAdapter;

const initialState = createAsyncEntityInitialState(entityAdapter.getInitialState());

const {reducer, actions, getSelectors} = createSlice({
  name: "consumption",
  initialState,
  reducers: {
    // default actions -- can be overwritten in the supplied reducers if needed.
    updateMany,
    updateOne,
    upsertOne,
    upsertMany,
    addOne,
    addMany,
    removeAll,
    removeOne,
    // default action
    clearPendingIds: (state: Draft<AsyncEntityState<ConsumptionsWithIdentifier, string>>) => {state.enqueueIds = []},
    addPendingId: (state: Draft<AsyncEntityState<ConsumptionsWithIdentifier, string>>, action: PayloadAction<Draft<string>>) => {
      state.enqueueIds.push(action.payload)
    },
    addLoadingIds: (state: Draft<AsyncEntityState<ConsumptionsWithIdentifier, string>>, action: PayloadAction<Draft<string[]>>) => {
      state.loadingIds.push(...action.payload)
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchByIdsThunk.pending, (state, action) => {
      state.status = "loading";
    })
    builder.addCase(fetchByIdsThunk.fulfilled, (state, action) => {
      state.status = "succeeded";
      entityAdapter.upsertMany(state, action.payload);
      const now = Date.now();
      
      for (const id of action.meta.arg) {
        (state.lastFetchedById as Record<string, number>)[id] = now;
      }
      state.loadingIds = state.loadingIds.filter((id) => !action.meta.arg.map((animal_id) => animal_id.toString()).includes(id));
      
    })
    builder.addCase(fetchByIdsThunk.rejected, (state, action) => {
      state.status = "failed"
      state.loadingIds = state.loadingIds.filter((id) => !action.meta.arg.map((animal_id) => animal_id.toString()).includes(id))
    })

    builder.addCase(fetchByFilterThunk.pending, (state, action) => {
      state.status = "loading";
    })
    builder.addCase(fetchByFilterThunk.fulfilled, (state, action) => {
      state.status = "succeeded";
      entityAdapter.upsertOne(state, action.payload);
      const now = Date.now();
      state.lastFetchedById[action.payload.target_id] = now;
      state.loadingIds = state.loadingIds.filter((id) => id !== action.payload.target_id)
    })
    builder.addCase(fetchByFilterThunk.rejected, (state, action) => {
      state.status = 'failed';
      state.loadingIds = state.loadingIds.filter((id) => id !== action.meta.arg.map(({key, ids}) => `${key}=${ids.join(",")}`).join("&"));
    })
  },
  selectors: {
    selectLoadingIds: createDraftSafeSelector(
    (state: AsyncEntityState<ConsumptionsWithIdentifier, string>) => state.loadingIds,
    (_, id: string) => id,
    (loadingIds: string[], id: string) => loadingIds.includes(id)
  ),
    selectConsumptionByMultipleIds: createDraftSafeSelector(
      (state: AsyncEntityState<ConsumptionsWithIdentifier, string>) => state.entities,
      (_, ids: string[]) => ids,
      (entities: Record<string, ConsumptionsWithIdentifier>, ids: string[]) => {
        return ids.reduce((agg, id) => {
          agg[id] = entities[id];
          return agg;
        }, {} as Record<string, ConsumptionsWithIdentifier>)
      }
    ),
    selectLoadingIdsByMultipleIds: createDraftSafeSelector(
      (state: AsyncEntityState<ConsumptionsWithIdentifier, string>) => state.loadingIds,
      (_, ids: string[]) => ids,
      (loadingIds: string[], ids: string[]) => {
        return ids.reduce((agg, id) => {
          agg[id] = loadingIds.includes(id);
          return agg;
        }, {} as Record<string, boolean>)  
      }
    )
  } 
})

const { clearPendingIds, addPendingId, addLoadingIds } = actions;
const stateSelector = (state: RootState) => state.graphQlData.consumption;
const consumptionFetcher = new EnqueueFetcherWithFilterFetch(fetchByIdsThunk, fetchByFilterThunk, stateSelector, clearPendingIds, addPendingId, addLoadingIds)
export const dispatchFetchByFilter = consumptionFetcher.fetchByFilters;
const dispatchFetchById = consumptionFetcher.enqueueFetchById;
const dispatchFetchByManyIds = consumptionFetcher.enqueueFetchByIds;
export const dispatchFetchConsumptionsByAnimalIds = consumptionFetcher.enqueueFetchByIds;

export const consumptionReducer = reducer as Reducer<AsyncEntityState<ConsumptionsWithIdentifier, string>>;
export const { selectById: selectConsumptionById } = entityAdapter.getSelectors(stateSelector);

const {selectLoadingIds, selectConsumptionByMultipleIds, selectLoadingIdsByMultipleIds} = getSelectors(stateSelector)
export const useAnimalConsumptionById = entityByIdWithStatusHookFactory(dispatchFetchById, selectConsumptionById, selectLoadingIds)
export const useAnimalConsumptionByMultipleIds = entitiesByMultipleIdsWithStatusHookFactory(dispatchFetchByManyIds, selectConsumptionByMultipleIds, selectLoadingIdsByMultipleIds )